import type {
  ManagedStrategySearchCompositionEntry,
  ManagedStrategySearchItem,
  ProductSearchItem,
} from "../../types/productV3";
import {
  extractProductContracts,
  resolveStrategyWeight,
  type StrategyPositionInput,
} from "../../utils/strategyFlatPositions";
import {
  getManagedCurrentComposition,
  isValidManagedChild,
} from "../../utils/managedStrategyRollup";

export interface AlphaStrategyPositionInput
  extends Omit<
    StrategyPositionInput,
    "entityType" | "sourceKey" | "sourceName"
  > {
  entityType: "PORTFOLIO" | "BASKET" | "MANAGED_STRATEGY";
}

export type AlphaProjectionIssueCode =
  | "MANAGED_NOT_FOUND"
  | "MANAGED_COMPOSITION_INVALID"
  | "PRODUCT_NOT_FOUND"
  | "PRODUCT_SNAPSHOT_INCOMPLETE"
  | "NESTED_BASKET_NOT_FOUND";

export interface AlphaProjectionIssue {
  code: AlphaProjectionIssueCode;
  sourceKey: string;
  message: string;
  dependencyKey?: string;
}

export interface AlphaExpandedStrategyProjection {
  expandedEntries: StrategyPositionInput[];
  complete: boolean;
  issues: AlphaProjectionIssue[];
}

function productKey(
  entityType: "PORTFOLIO" | "BASKET",
  productId: number
): string {
  return `${entityType}-${productId}`;
}

function sourceKey(entry: AlphaStrategyPositionInput): string {
  return `${entry.entityType}-${entry.productId}`;
}

function issueMessage(code: AlphaProjectionIssueCode): string {
  switch (code) {
    case "MANAGED_NOT_FOUND":
      return "A managed strategy could not be loaded.";
    case "MANAGED_COMPOSITION_INVALID":
      return "A managed strategy composition is unavailable.";
    case "PRODUCT_NOT_FOUND":
      return "A strategy product could not be loaded.";
    case "PRODUCT_SNAPSHOT_INCOMPLETE":
      return "Some strategy holdings are incomplete.";
    case "NESTED_BASKET_NOT_FOUND":
      return "A nested strategy product could not be loaded.";
  }
}

export function resolveAlphaStrategyWeight(
  entry: Pick<AlphaStrategyPositionInput, "weight" | "driftedWeight">
): number {
  return resolveStrategyWeight(entry);
}

// Composition-reading rules moved to utils/managedStrategyRollup so the admin
// rollup shares one definition of "current composition" and "valid child".
// Re-exported under their original names: every existing consumer is unchanged.
export const getAlphaManagedCurrentComposition = getManagedCurrentComposition;
export const isValidAlphaManagedChild = isValidManagedChild;

export function expandAlphaStrategyProjection(
  entries: AlphaStrategyPositionInput[],
  managedStrategies: ReadonlyMap<number, ManagedStrategySearchItem>,
  productItems: ProductSearchItem[],
  basketLookup: Map<number, ProductSearchItem>
): AlphaExpandedStrategyProjection {
  const expandedEntries: StrategyPositionInput[] = [];
  const issues: AlphaProjectionIssue[] = [];
  const issueKeys = new Set<string>();
  const itemByKey = new Map<string, ProductSearchItem>();
  for (const item of productItems) {
    itemByKey.set(productKey(item.productType, item.productId), item);
  }
  for (const item of basketLookup.values()) {
    if (item.productType === "BASKET") {
      itemByKey.set(productKey("BASKET", item.productId), item);
    }
  }

  const addIssue = (
    code: AlphaProjectionIssueCode,
    source: string,
    dependencyKey?: string
  ) => {
    const key = `${code}|${source}|${dependencyKey ?? ""}`;
    if (issueKeys.has(key)) return;
    issueKeys.add(key);
    issues.push({
      code,
      sourceKey: source,
      message: issueMessage(code),
      ...(dependencyKey ? { dependencyKey } : {}),
    });
  };

  for (const entry of entries) {
    const accountWeight = resolveAlphaStrategyWeight(entry);
    if (!Number.isFinite(accountWeight)) {
      addIssue(
        entry.entityType === "MANAGED_STRATEGY"
          ? "MANAGED_COMPOSITION_INVALID"
          : "PRODUCT_SNAPSHOT_INCOMPLETE",
        sourceKey(entry)
      );
      continue;
    }
    if (accountWeight === 0) continue;

    if (entry.entityType !== "MANAGED_STRATEGY") {
      expandedEntries.push({ ...entry, entityType: entry.entityType });
      continue;
    }

    const managedSourceKey = sourceKey(entry);
    const managed = managedStrategies.get(entry.productId);
    if (
      !managed ||
      managed.productType !== "MANAGED_STRATEGY" ||
      managed.productId !== entry.productId
    ) {
      addIssue("MANAGED_NOT_FOUND", managedSourceKey);
      continue;
    }

    if (managed.snapshot?.partial === true) {
      addIssue("PRODUCT_SNAPSHOT_INCOMPLETE", managedSourceKey);
      continue;
    }

    const composition = getAlphaManagedCurrentComposition(managed);
    if (composition === null || composition.length === 0) {
      addIssue("MANAGED_COMPOSITION_INVALID", managedSourceKey);
      continue;
    }

    const seenChildren = new Set<string>();
    for (const child of composition) {
      if (!isValidAlphaManagedChild(child)) {
        addIssue("MANAGED_COMPOSITION_INVALID", managedSourceKey);
        continue;
      }
      const key = productKey(child.entityType, child.productId);
      if (seenChildren.has(key)) {
        addIssue("MANAGED_COMPOSITION_INVALID", managedSourceKey, key);
        continue;
      }
      seenChildren.add(key);
      const childItem = itemByKey.get(key);
      if (childItem && childItem.providerId !== child.providerId) {
        addIssue("MANAGED_COMPOSITION_INVALID", managedSourceKey, key);
        continue;
      }
      expandedEntries.push({
        entityType: child.entityType,
        productId: child.productId,
        // Both inputs are percentage points. Do not normalize the composition.
        weight: (accountWeight * child.weight) / 100,
        displayName: childItem?.title ?? key,
        sourceKey: managedSourceKey,
        sourceName: entry.displayName,
      });
    }
  }

  const safeExpandedEntries: StrategyPositionInput[] = [];
  for (const entry of expandedEntries) {
    if (resolveAlphaStrategyWeight(entry) === 0) continue;
    const key = productKey(entry.entityType, entry.productId);
    const source = entry.sourceKey ?? key;
    const item = itemByKey.get(key);
    if (!item) {
      addIssue("PRODUCT_NOT_FOUND", source, key);
      continue;
    }

    const extracted = extractProductContracts(item, basketLookup, {
      strictDependencies: true,
    });
    for (const dependencyIssue of extracted.dependencyIssues) {
      addIssue(
        dependencyIssue.code,
        source,
        dependencyIssue.dependencyKey ?? dependencyIssue.productKey
      );
    }
    const topLevelSnapshotIsUnsafe = extracted.dependencyIssues.some((issue) => (
      issue.code === "PRODUCT_SNAPSHOT_INCOMPLETE"
      && issue.productKey === key
    ));
    if (topLevelSnapshotIsUnsafe) continue;
    safeExpandedEntries.push(entry);
  }

  return {
    // Exclude unsafe top-level snapshots. A parent with a missing nested
    // Basket remains so its known direct contracts can still be projected;
    // the Alpha hook removes the unsafe nested Basket from the leaf lookup.
    expandedEntries: safeExpandedEntries,
    complete: issues.length === 0,
    issues,
  };
}
